import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Activity, Stethoscope, Globe, Clock, Sparkles, BookOpen, Briefcase, ArrowRight, Calendar, Phone, CheckCircle } from 'lucide-react';
import { teamMembers, teamHighlights } from '../data/team';

const MedicalTeamSection = ({ showHeading = true }) => {
  const sectionPadding = showHeading ? 'py-20' : 'py-10';
  return (
    <section className={`${sectionPadding} relative overflow-hidden`}>
      <div className="absolute inset-0 bg-white"></div>

      <div className="w-full px-2 sm:px-4 lg:px-6 relative z-10">
        {showHeading && (
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-full font-semibold mb-6 shadow-lg">
              <Users className="w-5 h-5 mr-2" />
              Meet Our Experts
            </div>

            <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our Medical
              <span className="block bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                Team
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Meet the surgeons who personally lead every complex case at Sharma Ortho Center with evidence-backed protocols and warm bedside care.
            </p>
          </div>
        )}

        <div className="relative mb-6">
          <div className="hidden lg:block absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none" />
          <div className="hidden lg:block absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" />

          
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 lg:hidden">
          {teamHighlights.map((highlight, idx) => {
            const Icon = highlight.icon;
            return (
              <div
                key={idx}
                className="bg-white border border-blue-100 rounded-2xl p-5 shadow-md shadow-blue-100/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-blue-700" />
                </div>
                <p className="text-sm uppercase tracking-wide text-blue-500 font-semibold">{highlight.label}</p>
                <h4 className="text-2xl font-bold text-gray-900 mt-1">{highlight.value}</h4>
                <p className="text-gray-600 mt-2 text-sm leading-relaxed">{highlight.detail}</p>
              </div>
            );
          })}
        </div>

        <div className="space-y-12">
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className={`group flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-10 items-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-blue-300 p-4 sm:p-6 lg:p-10 hover:scale-[1.01] transform`}
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`
              }}
            >
              <div className="relative flex-shrink-0 w-full max-w-md lg:w-[26rem] h-80 sm:h-96 lg:h-[26rem] rounded-2xl overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200"></div>
                <img
                  src={member.image}
                  alt={member.name}
                  className="relative w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 hidden items-center justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Stethoscope className="w-16 h-16 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-400 rounded-full border-4 border-white animate-pulse"></div>
                  </div>
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-blue-600/20 to-transparent"></div>
              </div>

              <div className="flex-1 space-y-5 w-full px-6 py-6">
                <div className="rounded-2xl bg-gradient-to-r from-blue-600/95 via-blue-600 to-purple-600 text-white p-5 border border-blue-200/30 shadow-lg">
                  <div className="flex items-center gap-2 text-sm uppercase tracking-wider font-semibold text-blue-100">
                    <Stethoscope className="w-4 h-4" />
                    Consultant Spotlight
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold">{member.name}</h3>
                  <p className="text-blue-100 font-semibold">{member.title}</p>
                  {member.credentials && (
                    <p className="text-xs text-blue-100/80 mt-1">{member.credentials}</p>
                  )}
                </div>

                <p className="text-lg text-gray-600 leading-relaxed">
                  {member.bio}
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {member.educationTraining?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center">
                        <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                        Education & Training
                      </h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        {member.educationTraining.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {member.professionalExperience?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center">
                        <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                        Professional Experience
                      </h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        {member.professionalExperience.map((item, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {member.memberships?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center">
                      <Users className="w-4 h-4 mr-2 text-blue-600" />
                      Memberships
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {member.memberships.map((membership, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-white border border-blue-200 rounded-full text-sm font-semibold text-blue-700 shadow-sm"
                        >
                          {membership}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {member.expertise?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center">
                      <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
                      Areas of Expertise & Interest
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((area, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-semibold"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 text-gray-700">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold">Languages:</span>
                    <span>{member.languages.join(' • ')}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-700">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold">Availability:</span>
                    <span>{member.availability}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 pt-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl group-hover:scale-105 transform"
                  >
                    <Calendar className="mr-2 w-5 h-5" />
                    Book Consultation
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default MedicalTeamSection;

